import { useLicenseDetails } from "../../contexts/licenseDetails";
import Button from "../Button/Button";
import { useState } from "react";
import { PaymentMethodText } from "../../constants/billing";
import Select from "../Select/Select";
import Input from "../Input/Input";
import TextAreaInput from "../TextAreaInput/TextAreaInput";
import LicenseService from "../../services/LicensesService";
import {
   getZodFieldErrors,
   licensePaymentSchema,
} from "../../validations/license-subpages.schema";
import { LicenseBillingStatus } from "../../constants/licenses";
import { IoAlertCircleOutline } from "react-icons/io5";
import { useTranslation } from "react-i18next";

function getLocalDateTimeValue() {
   const now = new Date();
   return new Date(now.getTime() - now.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);
}

export function LicenseRegisterPaymentPage({ license }) {
   const { t } = useTranslation();
   const { setCurrentSubTab, setLicense } = useLicenseDetails();
   const [paymentMethod, setPaymentMethod] = useState("");
   const [paidAt, setPaidAt] = useState(getLocalDateTimeValue);
   const [description, setDescription] = useState("");
   const [errors, setErrors] = useState({});
   const [isSubmitting, setIsSubmitting] = useState(false);

   const billing = (() => {
      if (license.currentBilling && license.currentBilling.status !== LicenseBillingStatus.PAID) {
         return license.currentBilling;
      }
      return license.nextBilling;
   })();
   const hasPendingBilling = !billing || billing.status === LicenseBillingStatus.PAID;

   async function handleSubmit(event) {
      event.preventDefault();
      const billingUuid = billing?.externalId;
      const validation = licensePaymentSchema.safeParse({
         billingUuid: billingUuid ?? "",
         paymentMethod,
         paidAt,
      });
      if (!validation.success) {
         setErrors(getZodFieldErrors(validation.error));
         return;
      }
      setErrors({});

      setIsSubmitting(true);
      try {
         const response = await LicenseService.registerPayment(billingUuid, {
            billingUuid,
            paymentMethod,
            paymentSource: "MANUAL",
            paymentStatus: "PAID",
            paidAt: new Date(paidAt).toISOString(),
            metadata: description ? { description } : {},
         });
         if (response?.status >= 200 && response.status < 300) {
            const refreshedLicense = await LicenseService.getLicense(license.id);
            if (refreshedLicense?.status >= 200 && refreshedLicense.status < 300) {
               setLicense(refreshedLicense.data);
            }
            setCurrentSubTab(null);
         }
      } finally {
         setIsSubmitting(false);
      }
   }

   return (
      <div className="fade-in mt-0.5!">
         {hasPendingBilling && (
            <div className='alert alert-warning mb-3!'>
               <span className='inline-flex flex-row items-center flex-wrap gap-1.5'>
                  <b className='inline-flex flex-row items-center gap-1'>
                     <IoAlertCircleOutline size={18} />
                     {t("alerts.attention")}:
                  </b>
                  <span>Não há cobrança pendente de pagamento.</span>
               </span>
            </div>
         )}
         <div className="flex flex-row items-center justify-between">
            <h2 className="font-bold! text-[15px]!">Registrar pagamento</h2>
         </div>
         <p className="mt-3! opacity-60 leading-snug!">
            Registre o pagamento da licença.
         </p>
         {errors.billingUuid && (
            <span className="error-message mt-3!">{errors.billingUuid}</span>
         )}
         <form
            className="flex flex-col gap-3 mt-7!"
            onSubmit={handleSubmit}
         >
            <div className="grid grid-cols-2 gap-3">
               <Select
                  labelText="Forma de pagamento *"
                  value={paymentMethod}
                  onChange={({ target }) => {
                     setPaymentMethod(target.value);
                  }}
                  disabled={isSubmitting}
                  error={errors.paymentMethod}
                  options={Object.entries(PaymentMethodText).map(([method, label]) => ({
                     value: method,
                     text: label,
                  }))}
               />
               <Input
                  type={"datetime-local"}
                  name="paidAt"
                  id="paidAt"
                  labelText="Data e hora do pagamento *"
                  value={paidAt}
                  onChange={({ target }) => setPaidAt(target.value)}
                  disabled={isSubmitting}
                  error={errors.paidAt}
               />
            </div>

            <TextAreaInput
               name="description"
               id="description"
               labelText="Observações"
               value={description}
               onChange={({ target }) => setDescription(target.value)}
               disabled={isSubmitting}
            />

            <div className="w-full flex flex-row items-center justify-end gap-3! mt-6!">
               <Button
                  className={"button-white"}
                  label={"Cancelar"}
                  onClick={() => setCurrentSubTab(null)}
                  disabled={isSubmitting}
               />
               <Button
                  className={"button-blue"}
                  disabled={isSubmitting || hasPendingBilling}
                  label={"Salvar"}
                  type={"submit"}
                  isLoading={isSubmitting}
               />
            </div>
         </form>
      </div>
   );
}